/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
import React, { useEffect } from 'react';
import {
  useNavigate,
  useParams,
  useOutletContext,
  useLocation
} from 'react-router-dom';
// redux
import { useDispatch, useSelector } from 'react-redux';
// actions
import {
  getListing,
  getEditInstance,
  addInstance,
  updateInstance,
  deleteInstance,
  newInstance
} from '../../actions';
import { selectUser } from '../../store/userSlice';
// BASE components
import NavLinkAdapter from '../../../@BASE/core/NavLinkAdapter';
import BASELoading from '../../../@BASE/core/BASELoading';
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
// React hook form
import _ from '../../../@lodash';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
// Material UI Components
import Button from '@mui/material/Button';
import Box from '@mui/system/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
// Custom components
import NumberedHeading from '../forms/NumberedHeading';
import CommentScopeSelect from '../../main/comments/CommentScopeSelect';
import CommentStatusSelect from '../../main/comments/CommentStatusSelect';
import DeleteModal from '../crud/DeleteModal';

/**
 * Form Validation Schema
 */
const schema = yup.object().shape({
  content: yup.string().required('You must enter a comment')
});

const ReplyForm = ({ isIMI = false, collection = 'comments' }) => {
  const { instance, reviews } = useOutletContext();
  // const [reviews, setReviews] = React.useState(null);
  const [comment, setComment] = React.useState(null);
  const [reply, setReply] = React.useState(null);
  const user = useSelector(selectUser);
  const routeParams = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const pathArray = pathname.split('/');
  const resource = pathArray[1];
  const dispatch = useDispatch();
  const comment_heirarchy = ['project', 'course', 'lesson', 'section', 'page'];

  const dispatchString = collection;
  let navigateDestination;
  switch (collection) {
    case 'imi_comments':
      navigateDestination = resource === 'imi-comments' ? `/imi-comments/pages/${routeParams.id}${search}` : `/${resource}/${routeParams.id}/imi-comments${search}`;
      break;
    case 'pre_trial_comments':
      navigateDestination = resource === 'pre-trial-comments' ? `/pre-trial-comments/pages/${routeParams.id}${search}` : `/${resource}/${routeParams.id}/pre-trial-comments${search}`;
      break;
    default:
      navigateDestination = `/${resource}/${routeParams.id}/comments${search}`;
  }

  const {
    control,
    watch,
    reset,
    handleSubmit,
    formState,
    setValue,
    getValues
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const { isValid, dirtyFields, errors } = formState;

  const form = watch();

  const handleGetReply = async () => {
    try {
      const parentComment = await dispatch(
        getEditInstance(dispatchString)(routeParams.commentId)
      ).unwrap();

      setComment(parentComment);

      const response = await dispatch(
        routeParams.replyId === 'new'
          ? newInstance(dispatchString)()
          : getEditInstance(dispatchString)(routeParams.replyId)
      ).unwrap();

      setReply(response);
    } catch (err) {
      console.log(err.message);
    }
  };

  useEffect(() => {
    handleGetReply();
    // handleGetReviews();
  }, [dispatch, routeParams]);

  useEffect(() => {
    reset({ ...reply });
  }, [reply, reset]);

  function onSubmit(data) {
    const comment_heirarchy = [
      'project',
      'course',
      'lesson',
      'section',
      'page'
    ];
    // set user
    data.user = user._id;
    // default internality to false if user is not internal, otherwise use toggle
    data.internality = user.internal ? form.internality : false;
    data.scope = comment.scope;
    data.status = comment.status;
    // set project which is needed by backend
    // Loop through the hierarchy to navigate the nested structure
    let current = instance;
    for (let i = comment_heirarchy.length - 1; i >= 0; i--) {
      const key = comment_heirarchy[i];

      // If the current level exists, move deeper
      if (current && current[key]) {
        current = current[key];

        // If the key is "project" and has "id" or "_id", set it in data and exit the loop
        if (key === 'project') {
          if (current.id) {
            data.project = current.id;
          } else if (current._id) {
            data.project = current._id;
          }
          break; // Exit the loop once project.id or project._id is set
        }
      }
    }
    // loop through heirarchy in reverse till matching the intended scope to set information along with parents
    for (let i = comment_heirarchy.length - 1; i >= 0; i--) {
      const key = comment_heirarchy[i];

      // if comment lacks the key, skip this iteration
      if (!comment.hasOwnProperty(key)) {
        continue;
      }

      if (comment[key]['_id']) {
        data[key] = comment[key]['_id'];
      } else if (comment[key]['id']) {
        data[key] = comment[key]['id'];
      }
    }

    // get review period
    let currentReview = getCurrentReview();

    data.review = currentReview.id;
    data.title = comment.title;

    if (routeParams.replyId === 'new') {
      data.parent = routeParams.commentId;

      dispatch(addInstance(dispatchString)(data)).then(({ payload }) => {
        navigate(navigateDestination);
      });
    } else {
      const updatedReply = {
        id: routeParams.replyId,
        parent: routeParams.commentId,
        scope: data.scope,
        status: data.status,
        title: data.title,
        content: data.content,
        internality: data.internality,
        review: data.review
      };

      dispatch(updateInstance(dispatchString)(updatedReply)).then(({ payload }) => {
        navigate(navigateDestination);
      });
    }
  }

  const handleRemoveReply = () => {
    let data = {
      data: {
        user: user._id || user.id
      },
      id: reply.id
    };
    dispatch(deleteInstance(dispatchString)(data)).then(() => {
      navigate(navigateDestination);
    });
  };

  const getCurrentReview = () => {
    let currentDate = new Date(); // Keep full date-time for comparison

    let currentReview = reviews.find((review) => {
      let startDate = new Date(review.start);
      let endDate = new Date(review.end);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      return startDate <= currentDate && endDate >= currentDate;
    });

    return currentReview;
  };

  if (_.isEmpty(form) || !comment || !reply || !instance || !reviews) {
    return <BASELoading />;
  }

  return (
    <>
      <IconButton
        className="absolute top-0 right-0 my-16 mx-32 z-10"
        sx={{ color: 'black' }}
        component={NavLinkAdapter}
        to="../"
        size="large"
      >
        <BASESvgIcon>heroicons-outline:x</BASESvgIcon>
      </IconButton>
      <div className="relative flex flex-col flex-auto mt-64 px-24 sm:px-48">
        <Typography className="mt-12 text-2xl font-bold truncate">
          {routeParams.replyId === 'new' ? 'Create' : 'Edit'} Reply
        </Typography>
        <Divider className="mt-16 mb-24" />

        <NumberedHeading
          number="1"
          title="Scope"
          instructions="The scope of the parent comment follows."
        />
        <Controller
          name="scope"
          control={control}
          render={({ field }) => (
            <CommentScopeSelect
              className="mt-32"
              {...field}
              level={comment.scope}
              comment_heirarchy={comment_heirarchy}
              required
              disabled
            />
          )}
        />

        <NumberedHeading
          number="2"
          title="Parent Comment"
          instructions="This is the comment you are replying to."
        />
        <TextField
          className="mt-32 mb-16"
          id="parent_content"
          label="Content"
          type="text"
          multiline
          variant="outlined"
          fullWidth
          disabled
          value={comment.content}
        />

        <NumberedHeading
          number="3"
          title="Reply"
          instructions="Enter the reply using the controls below."
        />

        <Controller
          name="content"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              className="mt-32 mb-16"
              id="content"
              label="Content"
              type="text"
              multiline
              rows={10}
              error={!!errors.name}
              helperText={errors?.name?.message}
              variant="outlined"
              required
              fullWidth
            />
          )}
        />

        {user.internal && (
          <>
            <NumberedHeading
              number="4"
              title="Internality"
              instructions="Default to an internal reply. Turn off if not internal."
            />

            <Controller
              name="internality"
              control={control}
              defaultValue={user.internal || false}
              render={({ field }) => (
                <Switch
                  {...field}
                  id="internality"
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              )}
            />
          </>
        )}
      </div>
      <Box
        className="flex items-center mt-40 py-14 pr-16 pl-4 sm:pr-48 sm:pl-36 border-t"
        sx={{ backgroundColor: 'background.default' }}
      >
        {routeParams.replyId !== 'new' && (
          <DeleteModal onDelete={handleRemoveReply} resource="comment" />
        )}
        <Button className="ml-auto" component={NavLinkAdapter} to={-1}>
          Cancel
        </Button>
        <Button
          className="ml-8"
          variant="contained"
          color="secondary"
          disabled={_.isEmpty(dirtyFields) || !isValid}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </Box>
    </>
  );
};

export default ReplyForm;
